import { collection, DocumentSnapshot } from 'firebase/firestore';
import isEmpty from 'lodash/isEmpty';
import { collection as fsCollection } from 'rxfire/firestore';
import { BehaviorSubject, combineLatest, EMPTY, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  IMetric,
  IMetricMap,
  ISelectOption,
  ISelectOptionsMap,
  IUser,
} from '../types';
import {
  IMetricSettings,
  metricSettings,
} from './settings/metricSettingsProvider';
import { authenticatedUserProvider } from './userProvider';

export interface IMetrics {
  [testTypeId: string]: IMetricMap;
}

/**
 * @description hot observable of all the available metrics
 */
export const metrics = new BehaviorSubject<IMetrics>({});

/**
 * @description hot observable of only the active metrics
 */
export const activeMetrics = new BehaviorSubject<IMetrics>({});

/**
 * @description observes the metrics in the database
 */
authenticatedUserProvider()
  .pipe(
    switchMap((user: IUser): Observable<IMetrics> => {
      if (!user) return EMPTY;
      return fsCollection(collection(user.services.firestore, 'metrics')).pipe(
        map(
          (
            docs: DocumentSnapshot[]
          ): Array<{
            data: IMetricMap;
            id: string;
          }> => {
            return docs.map(
              (
                doc: DocumentSnapshot
              ): {
                data: IMetricMap;
                id: string;
              } => {
                const metricMap: IMetricMap = doc.data();
                return { data: metricMap, id: doc.id };
              }
            );
          }
        ),
        map(
          (
            MapArray: Array<{ data: IMetricMap; id: string }>
          ): {
            [id: string]: IMetricMap;
          } => {
            return MapArray.reduce(
              (
                acc: { [id: string]: IMetricMap },
                metricMap: { data: IMetricMap; id: string }
              ): IMetrics => {
                acc[metricMap.id] = metricMap.data;
                return acc;
              },
              {}
            );
          }
        )
      );
    })
  )
  .subscribe(metrics);

/**
 * @description start observing active metrics
 */
combineLatest([metrics, metricSettings])
  .pipe(
    map((results: [IMetrics, IMetricSettings]): IMetrics => {
      const [mMetrics, settings] = results;
      if (isEmpty(mMetrics) || isEmpty(settings)) return null;
      return Object.keys(mMetrics).reduce(
        (acc: IMetrics, testTypeId: string): IMetrics => {
          acc[testTypeId] = Object.keys(mMetrics[testTypeId]).reduce(
            (a: IMetricMap, metricKey: string): IMetricMap => {
              if (
                metricKey !== 'imperial' &&
                settings.metrics?.[testTypeId] &&
                settings.metrics?.[testTypeId][metricKey] === false
              ) {
                return a;
              }
              if (metricKey === 'id') {
                return a;
              }
              return {
                ...a,
                [metricKey]: mMetrics[testTypeId][metricKey],
              };
            },
            {}
          );
          return acc;
        },
        {}
      );
    })
  )
  .subscribe(activeMetrics);

export const isAsymmetryCalc = (metric: IMetric): boolean => {
  return (
    (/L\|R/.test(metric.label) || metric.category === 'Asymmetry') &&
    metric.units !== 'N' &&
    metric.units !== 'N/s'
  );
};

export const isLandingCalc = (metric: IMetric): boolean => {
  return metric.category === 'Landing';
};

/**
 * @description creates select options for metrics
 * @param hawkinMetricsMap
 * @param excludeAsymmetry
 * @returns
 */
export const toOptions = (
  hawkinMetricsMap: IMetrics,
  excludeAsymmetry?: boolean,
  onlyAsymmetry?: boolean,
  units?: string
): ISelectOptionsMap => {
  return Object.keys(hawkinMetricsMap).reduce(
    (acc: ISelectOptionsMap, curr: string): ISelectOptionsMap => {
      acc[curr] = Object.keys(hawkinMetricsMap[curr]).reduce(
        (a: ISelectOption[], key: string): ISelectOption[] => {
          /// if the metric is unknown, ignore it
          if (!hawkinMetricsMap[curr][key]) return a;
          /// if excludeAsymmetry is true and the metric is an asymmetry metric, ignore it
          if (
            excludeAsymmetry &&
            isAsymmetryCalc(hawkinMetricsMap[curr][key])
          ) {
            return a;
          }
          /// if onlyAsymmetry is true and the metric is not an asymmetry metric, ignore it
          if (onlyAsymmetry && !isAsymmetryCalc(hawkinMetricsMap[curr][key])) {
            return a;
          }

          const entity = hawkinMetricsMap[curr][key];
          /// check to see if units should match
          if (units != null && units !== entity.units) {
            return a;
          }
          const option = {
            label: entity.label,
            value: key,
            entity,
          };
          return a.concat([option]);
        },
        []
      );
      return acc;
    },
    {}
  );
};
